import classNames from 'classnames';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Form, Formik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    downloadLicenseCode,
    LicenseCodeDTO,
    useDeleteLicenseCodeApi,
    useFilterLicenseCodesApi,
    useSaveLicenseCodeApi,
    useSearchLicenseCodeApi,
} from '../../../api/license-code-api';
import SvgAdd from '../../../assets/icons/Add';
import SvgDownload from '../../../assets/icons/Download';
import SvgFilterList from '../../../assets/icons/FilterList';
import SvgPhotoPrint from '../../../assets/icons/PhotoPrint';
import SvgChevronLeft from '../../../assets/icons/ChevronLeft';
import SvgChevronRight from '../../../assets/icons/ChevronRight';
import SvgFirstPage from '../../../assets/icons/FirstPage';
import SvgLastPage from '../../../assets/icons/LastPage';
import SvgDelete from '../../../assets/icons/Delete';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { FormikTextField } from '../../../components/formik';
import { LoadingButton } from '../../../components/loading-button';
import { CreatedRowInfo } from '../../../components/table/created-row-info';
import { ModifiedRowInfo } from '../../../components/table/modified-row-info';
import { PageNavbar } from '../../../components/ui/page-navbar';
import { TProvider } from '../../../i18n';
import { IBasicTableColumn } from '../../../interfaces/interfaces';
import { GenerateLicenseCodeBulkModal } from './generate-license-code-bulk-modal';
import { LicenseCodeDownloadExcelModal } from './license-code-download-excel-modal';
import { SaveLicenseCodeModal } from './save-license-code-modal';

export function LicenseCodeApp() {
    const [showModal, setShowModal] = useState(false);
    const [showDownloadExcelModal, setShowDownloadExcelModal] = useState(false);
    const [showGenerateLicenseCodeBulkModal, setShowGenerateLicenseCodeBulkModal] = useState(false);
    const [selectedLicenseCode, setSelectedLicenseCode] = useState<LicenseCodeDTO | undefined>();
    const licenseCodeInit = useSearchLicenseCodeApi();
    const filterLicenseCodes = useFilterLicenseCodesApi();
    const saveLicenseCode = useSaveLicenseCodeApi();
    const deleteLicenseCode = useDeleteLicenseCodeApi();
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [totalCount, setTotalCount] = useState(0);
    
    // Multi-select state
    const [selectedLicenseCodes, setSelectedLicenseCodes] = useState<number[]>([]);
    
    // Filter state
    const [filterProductId, setFilterProductId] = useState<number>(0);
    const [filterBatchNumber, setFilterBatchNumber] = useState<string>('');

    const { result, error, busy, initialized, initializing } = licenseCodeInit;
    const { result: filterResult, error: filterError, initializing: filterInitializing } = filterLicenseCodes;

    const productTitle = useCallback((productId: number) => {
        const product = result?.products?.find(p => p.id === productId);
        if (!product) return <span>{productId}</span>;
        return <N>{product.name}</N>;
    }, [result?.products]);

    // Get all license codes (حذف فیلتر downloaded)
    const availableLicenseCodes = useMemo(() => {
        return filterResult?.licenseCodes || [];
    }, [filterResult?.licenseCodes]);

    // Check if all available codes are selected
    const allSelected = useMemo(() => {
        return availableLicenseCodes.length > 0 && 
               availableLicenseCodes.every(lc => selectedLicenseCodes.includes(lc.serial));
    }, [availableLicenseCodes, selectedLicenseCodes]);

    // Toggle single selection
    const handleToggleSelect = useCallback((serial: number) => {
        setSelectedLicenseCodes(prev => {
            if (prev.includes(serial)) {
                return prev.filter(s => s !== serial);
            } else {
                return [...prev, serial];
            }
        });
    }, []);

    // Toggle select all
    const handleToggleSelectAll = useCallback(() => {
        if (allSelected) {
            setSelectedLicenseCodes([]);
        } else {
            setSelectedLicenseCodes(availableLicenseCodes.map(lc => lc.serial));
        }
    }, [allSelected, availableLicenseCodes]);

    // Toggle select all codes in a batch
    const handleToggleSelectBatch = useCallback((batchNumber: string) => {
        const batchCodes = availableLicenseCodes
            .filter(lc => lc.batchNumber === batchNumber)
            .map(lc => lc.serial);
        
        const allBatchSelected = batchCodes.every(serial => selectedLicenseCodes.includes(serial));
        
        if (allBatchSelected) {
            // Deselect all codes in this batch
            setSelectedLicenseCodes(prev => prev.filter(s => !batchCodes.includes(s)));
        } else {
            // Select all codes in this batch
            setSelectedLicenseCodes(prev => [...new Set([...prev, ...batchCodes])]);
        }
    }, [availableLicenseCodes, selectedLicenseCodes]);

    const columns: IBasicTableColumn<LicenseCodeDTO>[] = useMemo(
        () => [
            {
                Header: (
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleToggleSelectAll}
                        disabled={availableLicenseCodes.length === 0}
                    />
                ),
                width: 40,
                Cell: ({ row }) => (
                    <input
                        type="checkbox"
                        checked={selectedLicenseCodes.includes(row.serial)}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleToggleSelect(row.serial);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                ),
            },
            {
                Header: <T>serial</T>,
                name: 'serial',
                width: 40,
                Cell: ({ row }) => <N>{row.serial}</N>,
            },
            {
                Header: <T>product</T>,
                name: 'productId',
                Cell: ({ row }) => productTitle(row.productId),
            },
            {
                Header: <T>batch-number</T>,
                name: 'batchNumber',
                width: 150,
                Cell: ({ row }) => (
                    row.batchNumber ? (
                        <span 
                            className="badge bg-info text-dark cursor-pointer"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleToggleSelectBatch(row.batchNumber!);
                            }}
                            title="کلیک کنید تا همه کدهای این دسته انتخاب شوند"
                        >
                            {row.batchNumber}
                        </span>
                    ) : (
                        <span className="badge bg-secondary text-dark">-</span>
                    )
                ),
            },
            {
                Header: <T>code</T>,
                name: 'code',
                Cell: ({ row }) => (
                    <bd.Flex align="center" gap={2}>
                        <span
                            className={classNames({
                                'text-decoration-line-through': row.downloaded,
                            })}
                        >
                            {row.code}
                        </span>
                        {row.downloaded && (
                            <small>
                                (<T>downloaded</T>)
                            </small>
                        )}
                    </bd.Flex>
                ),
            },
            {
                Header: <T>status</T>,
                name: 'downloaded',
                width: 100,
                Cell: ({ row }) => (
                    <bd.Flex align="center" gap={1}>
                        {row.downloaded ? (
                            <span className="badge bg-success"><T>used</T></span>
                        ) : (
                            <span className="badge bg-info"><T>available</T></span>
                        )}
                    </bd.Flex>
                ),
            },
            {
                Header: <T>created-by</T>,
                name: 'createdBy',
                width: 80,
                Cell: CreatedRowInfo,
            },
            {
                Header: <T>modified-by</T>,
                name: 'modifiedBy',
                width: 80,
                Cell: ModifiedRowInfo,
            },
        ],
        [productTitle, allSelected, handleToggleSelectAll, availableLicenseCodes, selectedLicenseCodes, handleToggleSelect, handleToggleSelectBatch]
    );

    useEffect(() => {
        if (initialized || initializing || error) return;
        licenseCodeInit.post(null, {});
    }, [initialized, initializing, error, licenseCodeInit]);

    // Load filtered license codes with optimization
    useEffect(() => {
        const timer = setTimeout(() => {
            filterLicenseCodes.post(null, {
                pageSize: pageSize,
                pageIndex: currentPage,
                productId: filterProductId || undefined,
                batchNumber: filterBatchNumber || undefined
            });
        }, 300);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize, currentPage, filterProductId, filterBatchNumber]);

    // Update total count when filter result changes
    useEffect(() => {
        if (filterResult) {
            setTotalCount(filterResult.totalCount);
        }
    }, [filterResult]);

    // Clear selection when page changes
    useEffect(() => {
        setSelectedLicenseCodes([]);
    }, [currentPage, pageSize]);

    const handleDelete = useCallback((licenseCode: LicenseCodeDTO) => {
        bd.msgbox(
            translate('delete-license-code'),
            translate('confirm-delete-license-code'),
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        deleteLicenseCode.remove(
                            licenseCode,
                            () => {
                                // Refresh the filtered license codes
                                filterLicenseCodes.post(null, {
                                    pageSize: pageSize,
                                    pageIndex: currentPage,
                                    productId: filterProductId || undefined,
                                    batchNumber: filterBatchNumber || undefined
                                });
                                notify.dark(
                                    translate('selected-license-code-deleted-successfully')
                                );
                                hide();
                                setShowModal(false);
                                setSelectedLicenseCode(undefined);
                            },
                            notify.error
                        );
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    }, [deleteLicenseCode, filterLicenseCodes, pageSize, currentPage, filterProductId]);

    // Bulk delete handler
    const handleBulkDelete = useCallback(() => {
        if (selectedLicenseCodes.length === 0) return;

        bd.msgbox(
            translate('delete-license-codes'),
            `${translate('confirm-delete')} ${selectedLicenseCodes.length} ${translate('license-codes')}?`,
            [
                {
                    title: translate('delete'),
                    variant: 'contained',
                    color: 'secondary',
                    action: hide => {
                        let completed = 0;
                        let failed = 0;

                        selectedLicenseCodes.forEach((serial) => {
                            const licenseCode = filterResult?.licenseCodes?.find(lc => lc.serial === serial);
                            if (licenseCode) {
                                deleteLicenseCode.remove(
                                    licenseCode,
                                    () => {
                                        completed++;
                                        if (completed + failed === selectedLicenseCodes.length) {
                                            // Refresh the list
                                            filterLicenseCodes.post(null, {
                                                pageSize: pageSize,
                                                pageIndex: currentPage,
                                                productId: filterProductId || undefined,
                                                batchNumber: filterBatchNumber || undefined
                                            });
                                            setSelectedLicenseCodes([]);
                                            notify.success(
                                                `${completed} ${translate('license-codes-deleted-successfully')}`
                                            );
                                            hide();
                                        }
                                    },
                                    () => {
                                        failed++;
                                        if (completed + failed === selectedLicenseCodes.length) {
                                            // Refresh the list
                                            filterLicenseCodes.post(null, {
                                                pageSize: pageSize,
                                                pageIndex: currentPage,
                                                productId: filterProductId || undefined,
                                                batchNumber: filterBatchNumber || undefined
                                            });
                                            setSelectedLicenseCodes([]);
                                            if (completed > 0) {
                                                notify.warning(
                                                    `${completed} ${translate('deleted')}, ${failed} ${translate('failed')}`
                                                );
                                            } else {
                                                notify.error(translate('delete-failed'));
                                            }
                                            hide();
                                        }
                                    }
                                );
                            }
                        });
                    },
                },
                {
                    title: translate('cancel'),
                    variant: 'outline',
                    color: 'secondary',
                },
            ],
            { className: 'modal-n', backdropClassName: 'modal-n' }
        );
    }, [selectedLicenseCodes, filterResult, deleteLicenseCode, filterLicenseCodes, pageSize, currentPage, filterProductId]);

    const handleSave = useCallback((licenseCode: LicenseCodeDTO) => {
        saveLicenseCode.post(
            null,
            licenseCode,
            res => {
                // Refresh the filtered license codes
                filterLicenseCodes.post(null, {
                    pageSize: pageSize,
                    pageIndex: currentPage,
                    productId: filterProductId || undefined,
                    batchNumber: filterBatchNumber || undefined
                });

                setShowModal(false);
                setSelectedLicenseCode(undefined);
                notify.success(
                    selectedLicenseCode
                        ? translate('selected-license-code-updated-successfully')
                        : translate('new-license-code-created-successfully')
                );
            },
            notify.error
        );
    }, [saveLicenseCode, filterLicenseCodes, pageSize, currentPage, selectedLicenseCode, filterProductId, filterBatchNumber]);

    const handleDownloadExcel = useCallback(async (license: LicenseCodeDTO) => {
        if (!result?.products) {
            notify.error(translate('product-not-found'));
            return;
        }
        const product = result.products.find(p => p.id === license.productId);
        if (!product) {
            notify.error(translate('product-not-found'));
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(product.name);

        worksheet.columns = [{ header: 'License Code', key: 'code', width: 30 }];
        worksheet.addRow(license);

        worksheet.getRow(1).getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        worksheet.getRow(1).getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF28A745' },
        };
        worksheet.getRow(1).getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${license.code}.xlsx`);
    }, [result?.products]);

    // Pagination handlers
    const handlePageChange = useCallback((newPage: number) => {
        setCurrentPage(newPage);
    }, []);

    const handlePageSizeChange = useCallback((newPageSize: number) => {
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when page size changes
    }, []);

    const totalPages = useMemo(() => Math.ceil(totalCount / pageSize), [totalCount, pageSize]);

    if (error || filterError) return <InitFailed ex={error || filterError} onRetry={() => {
        licenseCodeInit.post(null, {});
        filterLicenseCodes.post(null, { pageSize, pageIndex: currentPage, productId: filterProductId || undefined, batchNumber: filterBatchNumber || undefined });
    }} />;
    if (!initialized || initializing || filterInitializing) return <Loading />;

    return (
        <TProvider>
            <div className="container">
                <PageNavbar
                    onGoBack="/home"
                    title="custumer-license-code"
                    controls={
                        <bd.Flex gap={2} align="center">
                            {selectedLicenseCodes.length > 0 && (
                                <bd.Flex align="center" gap={2}>
                                    <span className="badge bg-primary">
                                        {selectedLicenseCodes.length} <T>selected</T>
                                    </span>
                                    <LoadingButton
                                        variant="contained"
                                        color="secondary"
                                        size="sm"
                                        label={<T>delete-selected</T>}
                                        icon={<SvgDelete />}
                                        onClick={handleBulkDelete}
                                    />
                                </bd.Flex>
                            )}
                            <LoadingButton
                                variant="outline"
                                size="sm"
                                label={<T>generate-license-code-bulk</T>}
                                icon={<SvgPhotoPrint />}
                                onClick={() => {
                                    setShowGenerateLicenseCodeBulkModal(true);
                                }}
                            />
                            <LoadingButton
                                variant="outline"
                                size="sm"
                                label={<T>download-excel</T>}
                                icon={<SvgDownload />}
                                onClick={() => {
                                    setShowDownloadExcelModal(true);
                                }}
                            />
                            <LoadingButton
                                variant="contained"
                                size="sm"
                                label={<T>new-license-code</T>}
                                icon={<SvgAdd />}
                                onClick={() => {
                                    setShowModal(true);
                                    setSelectedLicenseCode(undefined);
                                }}
                            />
                        </bd.Flex>
                    }
                />
                <bd.Paper className="h-100 p-3">
                    <Formik
                        initialValues={{ productId: 0, batchNumber: '' }}
                        onSubmit={values => {
                            setFilterProductId(values.productId);
                            setFilterBatchNumber(values.batchNumber);
                            setCurrentPage(1);
                        }}
                    >
                        <Form>
                            <bd.Flex align="center" gap={2}>
                                <FormikTextField
                                    name="productId"
                                    label={<T>product</T>}
                                    items={[
                                        { id: 0, title: '' },
                                        ...(result?.products?.map(p => ({ id: p.id, title: p.name })) || []),
                                    ]}
                                    width="11rem"
                                />
                                <FormikTextField
                                    name="batchNumber"
                                    label={<T>batch-number</T>}
                                    placeholder="BATCH-20251102-235312"
                                    width="15rem"
                                />
                                <LoadingButton
                                    variant="contained"
                                    type="submit"
                                    className="mt-2 min-w-80"
                                    icon={<SvgFilterList />}
                                    label={<T>filter</T>}
                                    loading={busy}
                                />
                            </bd.Flex>
                        </Form>
                    </Formik>
                    <bd.BasicTable
                        columns={columns}
                        data={filterResult?.licenseCodes || []}
                        className="mx-n3"
                        cellCSS={({ row }) => {
                            return row.downloaded ? 'bg-gray-3 text-muted opacity-50' : '';
                        }}
                        onRowClick={({ row }) => {
                            setSelectedLicenseCode(row);
                            setShowModal(true);
                        }}
                    >
                        <T as="div" className="nothing-found">
                            nothing-found
                        </T>
                    </bd.BasicTable>

                    {/* Pagination Controls */}
                    {totalCount > 0 && (
                        <bd.Flex justify="between" align="center" className="p-3 border-top">
                            <bd.Flex align="center" gap={2}>
                                <T>showing-{((currentPage - 1) * pageSize) + 1}-to-{Math.min(currentPage * pageSize, totalCount)}-of-{totalCount}</T>
                                <select
                                    className="form-select"
                                    value={pageSize}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handlePageSizeChange(Number(e.target.value))}
                                    style={{ width: '80px' }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                <T>per-page</T>
                            </bd.Flex>
                            
                            <bd.Flex align="center" gap={1}>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(1)}
                                    disabled={currentPage === 1}
                                    title={<T>first-page</T>}
                                >
                                    <SvgFirstPage className="rtl-rotate-180" />
                                </bd.Button>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    title={<T>previous-page</T>}
                                >
                                    <SvgChevronLeft className="rtl-rotate-180" />
                                </bd.Button>
                                <span className="px-2">
                                    <T>page-{currentPage}-of-{totalPages}</T>
                                </span>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    title={<T>next-page</T>}
                                >
                                    <SvgChevronRight className="rtl-rotate-180" />
                                </bd.Button>
                                <bd.Button
                                    variant="icon"
                                    size="sm"
                                    onClick={() => handlePageChange(totalPages)}
                                    disabled={currentPage === totalPages}
                                    title={<T>last-page</T>}
                                >
                                    <SvgLastPage className="rtl-rotate-180" />
                                </bd.Button>
                            </bd.Flex>
                        </bd.Flex>
                    )}

                    <SaveLicenseCodeModal
                        show={showModal}
                        products={result?.products || []}
                        onSave={handleSave}
                        onDelete={handleDelete}
                        selectedLicenseCode={selectedLicenseCode}
                        allowEdit={result?.allowEdit || false}
                        onHide={() => {
                            setShowModal(false);
                            setSelectedLicenseCode(undefined);
                        }}
                        onDownloadCode={license => {
                            handleDownloadExcel(license);

                            downloadLicenseCode(license.code).then(() => {
                                // Refresh the filtered license codes
                                filterLicenseCodes.post(null, {
                                    pageSize: pageSize,
                                    pageIndex: currentPage,
                                    productId: filterProductId || undefined,
                                    batchNumber: filterBatchNumber || undefined
                                });
                                setShowModal(false);
                                setSelectedLicenseCode(undefined);
                                notify.success(translate('license-code-downloaded-successfully'));
                            });
                        }}
                    />
                </bd.Paper>
            </div>

            <LicenseCodeDownloadExcelModal
                show={showDownloadExcelModal}
                products={result?.products || []}
                onHide={() => {
                    setShowDownloadExcelModal(false);
                    licenseCodeInit.reset();
                    filterLicenseCodes.post(null, { pageSize, pageIndex: currentPage, productId: filterProductId || undefined, batchNumber: filterBatchNumber || undefined });
                }}
            />
            <GenerateLicenseCodeBulkModal
                show={showGenerateLicenseCodeBulkModal}
                products={result?.products || []}
                onHide={() => {
                    setShowGenerateLicenseCodeBulkModal(false);
                    licenseCodeInit.reset();
                    filterLicenseCodes.post(null, { pageSize, pageIndex: currentPage, productId: filterProductId || undefined, batchNumber: filterBatchNumber || undefined });
                }}
            />
        </TProvider>
    );
}
